'use client';

import {
  useBlockPart,
  useMediaSource,
  type MediaSeriesDetail,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { EmptyState } from '../_shared/EmptyState';
import { VideoDetailLayout } from './VideoDetailLayout';
import { SeriesPlaylistSidebar } from './SeriesPlaylistSidebar';
import { sectionPad, sectionInner } from '../_shared/styles';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * /video/series/[slug]/play/[episodeSlug] — in-series watch page
 * with the playlist sidebar.
 */
export interface SeriesPlayerLayoutProps {
  seriesSlug: string;
  episodeSlug: string;
  episodeHrefTemplate?: string;
}

export function SeriesPlayerLayout({
  seriesSlug,
  episodeSlug,
  episodeHrefTemplate,
}: SeriesPlayerLayoutProps) {
  const part = useBlockPart();
  const media = useMediaSource();
  const state = useAsyncResource<{ series: MediaSeriesDetail | null }>(
    async (signal) => {
      if (!media) return null;
      const series = await media.series.fetchBySlug(seriesSlug, signal);
      if (series) return { series };
      // Series metadata missing — degrade to the bare episode if it exists.
      const episode = await media.videos.fetchBySlug(episodeSlug, signal);
      return episode ? { series: null } : null;
    },
    [media, seriesSlug, episodeSlug],
  );

  if (!media) {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <AdapterPlaceholder blockType="SeriesPlayerLayout" adapterName="MediaSource" />
        </div>
      </BlockRoot>
    );
  }
  if (state.kind === 'loading') return null;
  if (state.kind !== 'loaded') {
    return (
      <BlockRoot as="section" style={sectionPad}>
        <div style={sectionInner}>
          <EmptyState
            {...part('empty')}
            title={state.kind === 'error' ? 'Could not load this episode' : 'Episode not found'}
            hint={
              state.kind === 'error'
                ? 'Try again later.'
                : 'It may have been removed or unpublished.'
            }
          />
        </div>
      </BlockRoot>
    );
  }
  const { series } = state.data;
  if (!series) {
    // Episode exists but its series doesn't resolve — player without the playlist sidebar.
    return <VideoDetailLayout slug={episodeSlug} />;
  }
  const eps = series.episodes ?? [];

  return (
    <BlockRoot as="section" style={sectionPad}>
      <div
        style={{
          ...sectionInner,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 24,
        }}
      >
        <VideoDetailLayout slug={episodeSlug} />
        <SeriesPlaylistSidebar
          episodes={eps}
          activeEpisodeSlug={episodeSlug}
          seriesSlug={series.slug}
          {...(episodeHrefTemplate ? { episodeHrefTemplate } : {})}
        />
      </div>
    </BlockRoot>
  );
}
